import React from 'react';

import cs from 'classnames';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {DocumentMetadataStatusButton} from 'app/general/structures/DocumentMetadataStatusButton';
import {
  useSubscribe,
  useSubscriptionStatus,
  useUnsubscribe,
} from 'app/notifly/api';
import {useModelChangeSubscription} from 'app/subscriptions';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';

type Props<T> = {
  document: T;
  contentType:
    | 'procedure'
    | 'proceduregroup'
    | 'policy'
    | 'tasktemplate'
    | 'team';
  customTooltip?: (subscribed: boolean) => React.ReactNode;
};

export const DocumentSubscribedStatusButton = <
  T extends {
    id: number;
  }
>({
  document: {id},
  contentType,
  customTooltip,
}: Props<T>): JSX.Element => {
  const subscriptionParams = {object_id: id, content_type: contentType};
  const ctName = getContentType(contentType).name;

  const {
    data: subscriptionStatus,
    isFetching,
    refetch,
  } = useSubscriptionStatus(subscriptionParams);
  const [subscribe, {isLoading: isSubscribing}] = useSubscribe();
  const [unsubscribe, {isLoading: isUnsubscribing}] = useUnsubscribe();

  const subscriptionURL = subscriptionStatus?.results[0]?.url ?? '';
  const isSubscribed = subscriptionURL !== '';
  const loading = isFetching || isSubscribing || isUnsubscribing;

  // model-change ws messages use 'process' as ct, not 'processgroup'
  const normalizedCt =
    contentType === 'proceduregroup' ? 'process' : contentType;

  useModelChangeSubscription(
    (message, {runWhenAny, modelIdMatch, anyRelatedMatch}) =>
      runWhenAny(
        message,
        [
          modelIdMatch(normalizedCt, id),
          anyRelatedMatch({
            content_type: normalizedCt,
            id,
          }),
        ],
        refetch
      ),
    [id, normalizedCt]
  );

  const updateSubscription = () => {
    if (isSubscribed) {
      unsubscribe({...subscriptionParams, url: subscriptionURL});
    } else {
      subscribe(subscriptionParams);
    }
  };

  return (
    <LazyTooltip
      tooltip={
        customTooltip?.(isSubscribed) ??
        (isSubscribed
          ? gt(
              `You’re receiving notifications because you’re subscribed to this ${ctName}.`
            )
          : gt(`You’re not receiving notifications from this ${ctName}.`))
      }
    >
      <DocumentMetadataStatusButton
        disabled={loading}
        active={isSubscribed}
        onClick={updateSubscription}
      >
        <i
          className={cs(
            'fa',
            'fa-fw',
            loading
              ? 'fa-circle-notch fa-spin'
              : isSubscribed
              ? 'fa-bell'
              : 'fa-bell-slash'
          )}
        />
      </DocumentMetadataStatusButton>
    </LazyTooltip>
  );
};
